import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FiUsers, FiClock, FiTrendingUp, FiBell, FiCalendar, FiCheckCircle, FiXCircle, FiArrowUpRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { dashboardAPI } from '../../services/api';
import { formatDate, getStatusColor } from '../../utils/helpers';
import AdminLayout from '../../components/layout/AdminLayout';

const STATUS_COLORS = { Pending: '#f59e0b', Approved: '#10b981', Rejected: '#ef4444' };

const StatCard = ({ icon: Icon, label, value, subtext, color = 'primary', trend }) => (
  <motion.div
    whileHover={{ y: -3 }}
    className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm"
  >
    <div className="flex items-start justify-between mb-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
        color === 'primary' ? 'bg-primary-50 text-primary-500' :
        color === 'yellow' ? 'bg-yellow-50 text-yellow-500' :
        color === 'green' ? 'bg-green-50 text-green-500' :
        color === 'blue' ? 'bg-blue-50 text-blue-500' :
        'bg-purple-50 text-purple-500'
      }`}>
        <Icon className="w-5 h-5" />
      </div>
      {trend && (
        <span className="flex items-center gap-1 text-xs text-green-500 bg-green-50 px-2 py-0.5 rounded-full">
          <FiArrowUpRight className="w-3 h-3" />
          {trend}
        </span>
      )}
    </div>
    <div className="text-3xl font-bold text-primary-500 font-serif mb-1">{value}</div>
    <div className="text-gray-700 font-medium text-sm">{label}</div>
    {subtext && <p className="text-gray-400 text-xs mt-0.5">{subtext}</p>}
  </motion.div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, thisMonth: 0, notices: 0, events: 0 });
  const [chartData, setChartData] = useState([]);
  const [admissions, setAdmissions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await dashboardAPI.getStats();
        const d = res.data?.data;
        if (d) {
          setStats({
            total: d.summary?.admissions?.total || 0,
            pending: d.summary?.admissions?.pending || 0,
            approved: d.summary?.admissions?.approved || 0,
            rejected: d.summary?.admissions?.rejected || 0,
            thisMonth: d.summary?.admissions?.thisMonth || 0,
            notices: d.summary?.notices?.published || 0,
            events: d.summary?.events?.upcoming || 0,
          });
          const trend = d.charts?.monthlyAdmissionTrend;
          setChartData((trend || []).map(t => ({ month: t.monthName, admissions: t.total })));
          const recent = d.recentActivity?.admissions;
          setAdmissions((recent || []).map(a => ({
            id: a._id,
            studentName: a.studentName,
            classApplying: a.classApplyingFor,
            email: a.email,
            status: a.status,
            createdAt: a.createdAt,
          })));
        }
      } catch {
        toast.error('Failed to load dashboard statistics');
      }
    };
    fetchData();
  }, []);

  const statusData = [
    { name: 'Pending', value: stats.pending, color: STATUS_COLORS.Pending },
    { name: 'Approved', value: stats.approved, color: STATUS_COLORS.Approved },
    { name: 'Rejected', value: stats.rejected, color: STATUS_COLORS.Rejected },
  ];
  const hasStatusData = stats.pending + stats.approved + stats.rejected > 0;

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <AdminLayout>
      {/* Welcome */}
      <div className="mb-8">
        <h2 className="text-2xl font-serif font-bold text-primary-500">Dashboard Overview</h2>
        <p className="text-gray-500 text-sm mt-1">{today}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={FiUsers} label="Total Admissions" value={stats.total} subtext="All time" color="primary" trend="+12%" />
        <StatCard icon={FiClock} label="Pending Review" value={stats.pending} subtext="Awaiting action" color="yellow" />
        <StatCard icon={FiTrendingUp} label="This Month" value={stats.thisMonth} subtext="March 2025" color="green" trend="+28%" />
        <StatCard icon={FiBell} label="Active Notices" value={stats.notices} subtext="Published" color="blue" />
        <StatCard icon={FiCalendar} label="Upcoming Events" value={stats.events} subtext="Next 30 days" color="purple" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Bar Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h3 className="font-bold text-primary-500 mb-1">Monthly Admissions</h3>
          <p className="text-gray-400 text-xs mb-5">Last 6 months overview</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} barCategoryGap="40%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
                cursor={{ fill: 'rgba(26,58,92,0.05)' }}
              />
              <Bar dataKey="admissions" fill="#2563EB" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h3 className="font-bold text-primary-500 mb-1">Admission Status</h3>
          <p className="text-gray-400 text-xs mb-5">Current distribution</p>
          {hasStatusData ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {statusData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Legend
                  formatter={(value) => <span style={{ fontSize: '11px', color: '#6b7280' }}>{value}</span>}
                />
                <Tooltip contentStyle={{ borderRadius: '10px', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
              No admissions yet
            </div>
          )}
        </div>
      </div>

      {/* Recent Admissions Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-primary-500">Recent Admissions</h3>
            <p className="text-gray-400 text-xs mt-0.5">Latest 5 applications</p>
          </div>
          <a href="/admin/admissions" className="text-primary-500 text-sm font-medium hover:text-secondary-500 transition-colors">
            View All →
          </a>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-50">
                <th className="text-left px-6 py-3 text-gray-500 font-medium">Student</th>
                <th className="text-left px-6 py-3 text-gray-500 font-medium hidden md:table-cell">Class</th>
                <th className="text-left px-6 py-3 text-gray-500 font-medium hidden lg:table-cell">Parent</th>
                <th className="text-left px-6 py-3 text-gray-500 font-medium">Status</th>
                <th className="text-left px-6 py-3 text-gray-500 font-medium hidden sm:table-cell">Date</th>
              </tr>
            </thead>
            <tbody>
              {admissions.map((adm, idx) => (
                <tr key={adm.id || idx} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-800">{adm.studentName}</div>
                    <div className="text-xs text-gray-400">{adm.email}</div>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell text-gray-600">{adm.classApplying}</td>
                  <td className="px-6 py-4 hidden lg:table-cell text-gray-600">{adm.parentName}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(adm.status)}`}>
                      {adm.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell text-gray-500 text-xs">
                    {formatDate(adm.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {admissions.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">
              No admissions yet
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
